// The online half of the menu: the `RoomClient` and its transport, the
// create-room form, and the single handoff from the menu to the board. A room
// filling, a link resume and a reload all arrive as the same thing —
// `RoomClient` reaching `ready` — so the screens never navigate to the game
// themselves; this class watches the room and mounts `GameScene` once.
import {
  ConnectionManager, RoomClient, SessionStore, IDENTITY_ROOM_GRACE_WINDOW_MS, clearRoomHash,
  type LobbyInfo, type SessionEntry,
} from '@bn-games/room-client';
import { getWsTicket } from '@bn-games/auth-client';
import { ActiveGameStore } from '../history/ActiveGameStore.js';
import type { ActiveSession } from '../history/types.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { OnlineMenuApi, Scene } from '../ui/types.js';
import type { GameScene } from './GameScene.js';
import type { ChessRoom } from './OnlineMode.js';
import {
  DEFAULT_ROOM_FORM, ROOM_VARIANT, SESSION_STORAGE_KEY, preferredSeatFor, resolveServerUrl, roomOptionsFor,
  type OnlineRoomForm,
} from './onlineHostConfig.js';
import { recordResignationOnLeave } from './leaveResult.js';
import { performRoomResume } from './onlineResume.js';

export interface OnlineMenuHostDeps {
  readonly gameScene: GameScene;
  readonly settings: SettingsManager;
  readonly navigate: (scene: Scene) => void;
}

export class OnlineMenuHost implements OnlineMenuApi {
  private readonly connection: ConnectionManager<ClientMessage, ServerMessage>;
  private readonly room: ChessRoom;
  private readonly sessionStore: SessionStore;
  private form: OnlineRoomForm = DEFAULT_ROOM_FORM;
  private atTable = false;
  /** Set while a resignation sent on the way out awaits the server; calling it drops the room now. */
  private finishLeaving: (() => void) | null = null;

  constructor(private readonly deps: OnlineMenuHostDeps) {
    this.connection = new ConnectionManager<ClientMessage, ServerMessage>({ serverUrl: resolveServerUrl() });
    // Resolved at connect time, never at screen-mount time: a ticket lives
    // 60s and the online screens routinely sit open longer, which would seat
    // a signed-in player anonymously for the life of the room.
    this.connection.setTicketProvider(getWsTicket);
    this.sessionStore = new SessionStore(SESSION_STORAGE_KEY, IDENTITY_ROOM_GRACE_WINDOW_MS);
    this.room = new RoomClient<ClientMessage, ServerMessage>(this.connection, this.sessionStore);
    this.room.onUpdate((info) => this.onRoomInfo(info));
  }

  getRoomInfo = (): LobbyInfo => this.room.getInfo();

  onRoomUpdate = (listener: () => void): (() => void) => this.room.onUpdate(() => listener());

  getForm = (): OnlineRoomForm => this.form;

  setForm = (partial: Partial<OnlineRoomForm>): void => {
    this.form = { ...this.form, ...partial };
  };

  createRoom = (): void => {
    this.finishLeaving?.();
    this.room.createRoom(
      this.playerName(), ROOM_VARIANT, preferredSeatFor(this.form.color), undefined, undefined, roomOptionsFor(this.form),
    );
  };

  joinRoom = (code: string): void => {
    this.finishLeaving?.();
    this.room.joinRoom(code, this.playerName());
  };

  /**
   * Back out of a room that has not started (create / join screens). Nothing
   * saved for a game in progress is touched: that belongs to another room.
   */
  leaveRoom = (): void => {
    this.room.cancel();
  };

  resumeRoom = (roomCode: string, legacyToken?: string): Promise<void> => {
    this.finishLeaving?.();
    return performRoomResume({
      room: this.room,
      sessionStore: this.sessionStore,
      playerName: () => this.playerName(),
      onSeatLost: () => this.forget(),
    }, roomCode, legacyToken);
  };

  getSavedSession = (): SessionEntry | null => this.sessionStore.load();

  /** The saved game matching the saved seat, for the resume button's label. */
  getActiveSession = (): ActiveSession | null => {
    const active = ActiveGameStore.peek();
    return active !== null && active.roomCode === this.sessionStore.load()?.roomCode ? active : null;
  };

  /** True once the room has put us at a board, until we leave it. */
  isAtTable(): boolean {
    return this.atTable;
  }

  /**
   * Leave the table for good: resign a game still in progress (the opponent
   * should not be left waiting on a seat nobody will return to), then drop
   * the room and everything saved for it. Idempotent.
   */
  leaveTable(): void {
    if (!this.atTable) return;
    this.atTable = false;
    if (!this.deps.gameScene.isOnlineGameLive()) {
      this.disconnect();
      return;
    }
    this.deps.gameScene.resign();
    // The loss is recorded from the server's answer, so the room is held until it arrives.
    this.finishLeaving = recordResignationOnLeave(this.connection, () => {
      this.finishLeaving = null;
      this.disconnect();
    });
  }

  private disconnect(): void {
    this.atTable = false;
    // Clears this room's saved seat token as well.
    this.room.cancel();
    this.forget();
  }

  private forget(): void {
    ActiveGameStore.clear();
    clearRoomHash();
  }

  private playerName(): string {
    return this.deps.settings.get().playerName;
  }

  private onRoomInfo(info: LobbyInfo): void {
    if (info.state !== 'ready' || this.atTable || this.finishLeaving !== null) return;
    this.atTable = true;
    // Navigate first so the canvas is visible before the scene starts its rAF loop.
    this.deps.navigate('game');
    this.deps.gameScene.mount({
      kind: 'online',
      room: this.room,
      onLeave: () => {
        this.leaveTable();
        this.deps.navigate('menu');
      },
    });
  }
}
